import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  flowReading,
  flowToggleKey,
  contextHoldS,
  type FlowLevelKey,
  type FlowReading,
  type FlowSignal,
} from '../../store/flowSignalsStore';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

/**
 * Order-flow markers (flow-signals via the engine) on the main series.
 *
 * Each signal sits at its own price on the displayed candle that contains it:
 * bullish readings just below the price, bearish just above, one glyph per
 * kind, larger the further its value cleared the trigger. Every glyph sits on
 * a dark semi-opaque backing disc ringed green or red by its reading, so it
 * stays legible on any candle (a 🛑 on a red body, a 🧹 on a green one). Only
 * signals inside the visible time range are placed; when one candle has more
 * than CLUSTER_AT on one side they fold into a single glyph (the strongest)
 * with a count badge.
 */

export type FlowShape =
  | 'emoji'
  | 'diamond'
  | 'triangle'
  | 'chevron'
  | 'doubleChevron'
  | 'circle'
  | 'star'
  | 'square'
  | 'hexagon'
  | 'bar';

export interface FlowGlyph {
  /** 'emoji' draws `symbol` as text; anything else is a canvas path. */
  shape: FlowShape;
  /** What the panel shows for this kind (the emoji itself, or a look-alike character). */
  symbol: string;
  /** Outline only (paths). */
  hollow: boolean;
  /** Drawn at reduced alpha (traces, cancelled icebergs). */
  faint: boolean;
}

const glyph = (shape: FlowShape, symbol: string, hollow = false, faint = false): FlowGlyph => ({ shape, symbol, hollow, faint });

/**
 * kind -> glyph, the one place to change how a kind looks (chart, TradeFinder
 * switches and recent-signals list all read it). Emoji carry their own
 * colour, so the reading is shown by the green / red ring of the backing disc.
 */
export const FLOW_GLYPHS: Record<string, FlowGlyph> = {
  sweep: glyph('emoji', '🧹'),
  passive_sweep_trace: glyph('emoji', '🧹', false, true),
  stop_sweep_trace: glyph('emoji', '🧹', false, true),
  absorption: glyph('emoji', '🍪'),
  stop_run: glyph('emoji', '🛑'),
  stop_volume: glyph('circle', '●'),
  volume_spike: glyph('star', '★'),
  imbalance_signal: glyph('square', '■'),
  iceberg: glyph('hexagon', '⬢'),
  iceberg_development: glyph('hexagon', '⬡', true),
  iceberg_cancelled: glyph('hexagon', '⬡', true, true),
  book_imbalance: glyph('bar', '▬'),
  liquidity: glyph('bar', '▭', true),
  // Context (unproven): a sweep that kept extending.
  sweep_follow: glyph('emoji', '⏩'),
  // Context (MBO research, display only): plain shapes pointing the way the
  // research says price tended to go, each with the 60 s context timer ring.
  burst_level: glyph('doubleChevron', '⇈'),
  abs_break_level: glyph('triangle', '▲'),
  queue_depletion: glyph('chevron', '⌃'),
};

/** queue_depletion is the busiest context kind (ES/MES): drawn this much smaller. */
export const DEPLETION_SCALE = 0.75;

const UNKNOWN_GLYPH = glyph('circle', '○', true);

/** Emoji-capable font stack for the emoji glyphs. */
export const EMOJI_FONT = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';

export interface FlowItem {
  id: string;
  /** Displayed bar start, unix seconds. */
  time: number;
  price: number;
  reading: FlowReading;
  glyph: FlowGlyph;
  /** Glyph half-size, px. */
  size: number;
  color: string;
  /** 0..1 draw opacity (recency fade x edge dimming); 1 when absent. */
  alpha?: number;
  /** On a key level: drawn larger (already in `size`) with a soft glow. */
  emphasis?: boolean;
  /** Small label beside the glyph, e.g. "🍪 at PW". */
  tag?: string;
  /** Epoch ms until which the marker pulses (the newest signal). */
  pulseUntil?: number;
  /** Context kinds: the timer ring over the research horizon, from the signal time for holdMs. */
  hold?: FlowHold;
  /** Phone / narrow chart: smaller glow and label. */
  compact?: boolean;
}

/** A timer ring that depletes from `startMs` over `holdMs` (wall-clock ms, like the pulse). */
export interface FlowHold {
  startMs: number;
  holdMs: number;
}

export interface FlowPlaced {
  /** Signal ids under this glyph (more than one for a cluster). */
  ids: string[];
  x: number;
  y: number;
  size: number;
  glyph: FlowGlyph;
  reading: FlowReading;
  color: string;
  count: number;
  alpha?: number;
  emphasis?: boolean;
  tag?: string;
  pulseUntil?: number;
  hold?: FlowHold;
  compact?: boolean;
  /** Briefly highlighted (strip click). */
  flash?: boolean;
}

/** More than this many on one candle side fold into a count badge. */
export const CLUSTER_AT = 3;
/** Emoji font size, CSS px: the weakest signal and the strongest. */
export const EMOJI_MIN_PX = 14;
export const EMOJI_MAX_PX = 20;
/** Shape glyph diameter, CSS px: the weakest signal and the strongest. */
export const SHAPE_MIN_PX = 12;
export const SHAPE_MAX_PX = 18;
/** Phones / narrow charts: emoji 12-13px, shapes 10-12px (disc ~16-18px). */
export const COMPACT_EMOJI_MIN_PX = 12;
export const COMPACT_EMOJI_MAX_PX = 13;
export const COMPACT_SHAPE_MIN_PX = 10;
export const COMPACT_SHAPE_MAX_PX = 12;
/**
 * A shape is drawn at this fraction of its half-size inside the disc, so a dark
 * band always separates its colour from the candle under it (a red dot on a
 * red body would otherwise merge into it).
 */
export const SHAPE_INSET = 0.72;
/** Shape half-size bounds, px (FlowItem.size for shape glyphs). */
export const MIN_SIZE = SHAPE_MIN_PX / 2;
export const MAX_SIZE = SHAPE_MAX_PX / 2;
/** Emoji half-size bounds, px (FlowItem.size for emoji glyphs). */
export const EMOJI_MIN_SIZE = EMOJI_MIN_PX / 2;
export const EMOJI_MAX_SIZE = EMOJI_MAX_PX / 2;
/** value / trigger at which a glyph reaches its maximum size is e^this. */
const STRENGTH_LN = 2.5;
/** Backing disc radius = glyph half-size + this, px. */
export const DISC_PAD = 2;
/** Backing disc fill: dark, semi-opaque. */
export const DISC_FILL = 'rgba(10,15,30,0.85)';
/** Reading ring width, px. */
export const RING_PX = 1.5;
/** px between the price and the backing disc's near edge. */
export const FLOW_GAP = 2;
/** Hover tolerance beyond the drawn disc + ring, px. */
export const HIT_PAD = 4;

/** Hover radius of a glyph of half-size `size`: the drawn disc and its ring, plus HIT_PAD. */
export function flowHitRadius(size: number): number {
  return size + DISC_PAD + RING_PX + HIT_PAD;
}
export const BADGE_FONT_PX = 10;
/** A marker on a key level is drawn this much larger (desktop / compact). */
export const EMPHASIS_SCALE = 1.3;
export const COMPACT_EMPHASIS_SCALE = 1.2;
/** Soft glow around an emphasised marker's disc, CSS px blur (desktop / compact). */
export const GLOW_BLUR_PX = 5;
export const COMPACT_GLOW_BLUR_PX = 2;
/** Level code ("PW") font size, CSS px (desktop / compact). */
export const TAG_FONT_PX = 10;
export const COMPACT_TAG_FONT_PX = 9;
/** Cluster count superscript on the disc's top-right, CSS px. */
export const SUPER_FONT_PX = 8;
/** One pulse ring cycle, ms. */
export const PULSE_PERIOD_MS = 1_200;
/** How long a strip click highlights its marker, ms. */
export const FLASH_MS = 2_400;
/** Opacity never drops below this, so a faded marker is still legible. */
export const MIN_ALPHA = 0.15;
/** Timer ring: px outside the backing disc, and its stroke width. */
export const HOLD_RING_GAP = 3;
export const HOLD_RING_PX = 1.5;
/** While only timer rings animate, repaint this often, ms (a 60 s ring needs no 60 fps). */
export const HOLD_REPAINT_MS = 250;
/**
 * sweep_follow outside 09:00-11:00 PT (detail.midday false) was drawn at this
 * opacity. No longer applied: the full-history research found no midday
 * premium, and sweep_follow itself is unproven.
 */
export const OFF_MIDDAY_ALPHA = 0.7;

/**
 * Remaining fraction of a timer ring: 1 at the signal, 0 once `holdS` has
 * passed (and for a non-positive hold); a signal time in the future (clock
 * skew) counts as just fired.
 */
export function holdFraction(now: number, signalTs: number, holdS: number): number {
  if (!(holdS > 0) || !Number.isFinite(now) || !Number.isFinite(signalTs)) return 0;
  const left = 1 - (now - signalTs) / (holdS * 1000);
  return left <= 0 ? 0 : Math.min(1, left);
}

/** Context opacity of a sweep_follow: full inside 09:00-11:00 PT, OFF_MIDDAY_ALPHA when detail.midday is false, 1 when unknown. */
export function middayAlpha(detail: FlowSignal['detail']): number {
  return detail?.midday === false ? OFF_MIDDAY_ALPHA : 1;
}

/** Font of the optional tag beside a glyph (measured for the layout box). */
export const FLOW_TAG_FONT = '600 10px system-ui,-apple-system,"Segoe UI",Roboto,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji"';
/** Px between the disc (or count badge) and the tag. */
export const FLOW_TAG_GAP = 4;
/** Horizontal padding inside the tag pill, each side, px. */
export const FLOW_TAG_PAD = 4;

// ------------------------------------------------------------- mapping

/** Bar start (unix seconds) of the displayed candle that contains `ts` (ms). */
export function flowBarTime(ts: number, tfMinutes: number): number {
  const step = Math.max(60_000, tfMinutes * 60_000);
  return Math.floor((Math.floor(ts / step) * step) / 1000);
}

export function flowGlyph(s: Pick<FlowSignal, 'kind'>): FlowGlyph {
  return FLOW_GLYPHS[s.kind] ?? UNKNOWN_GLYPH;
}

/** Default trigger per kind (flow-signals / TradeFinder defaults). */
const DEFAULT_TRIGGER: Record<string, number> = {
  absorption: 1000,
  sweep: 1000,
  passive_sweep_trace: 1000,
  stop_sweep_trace: 200,
  stop_run: 200,
  stop_volume: 150,
  volume_spike: 800,
  imbalance_signal: 500,
  iceberg: 300,
  iceberg_development: 300,
  iceberg_cancelled: 300,
  book_imbalance: 20,
  liquidity: 300,
};

/** The threshold the signal's value cleared: from the detail when the service sent it. */
export function flowTrigger(s: Pick<FlowSignal, 'kind' | 'detail'>): number | null {
  const d = s.detail ?? {};
  for (const k of ['trigger', 'triggerPct']) {
    const v = d[k];
    if (typeof v === 'number' && v > 0) return v;
  }
  if (s.kind === 'sweep' && d.stop === true) return 200;
  if (s.kind === 'liquidity' && d.type === 'absolute') return 500;
  // value = +/- levels swept; the size grows with levels past the minimum.
  if (s.kind === 'sweep_follow') {
    const m = d.min_levels;
    return typeof m === 'number' && m > 0 ? m : 5;
  }
  // MBO context kinds: value = +/- the research ratio; size grows past its gate.
  if (s.kind === 'burst_level' || s.kind === 'abs_break_level') {
    const r = d.trigger_ratio;
    return typeof r === 'number' && r > 0 ? r : s.kind === 'burst_level' ? 4 : 0.15;
  }
  if (s.kind === 'queue_depletion') return 2;
  return DEFAULT_TRIGGER[s.kind] ?? null;
}

/** 0 at or below the trigger, rising with log(value / trigger), 1 at e^2.5 x the trigger. */
export function flowStrength(s: Pick<FlowSignal, 'kind' | 'detail' | 'value'>): number {
  const trigger = flowTrigger(s);
  const ratio = trigger ? Math.abs(s.value) / trigger : 0;
  if (!Number.isFinite(ratio) || ratio <= 1) return 0;
  return Math.min(1, Math.log(ratio) / STRENGTH_LN);
}

/**
 * Glyph half-size, px: emoji from EMOJI_MIN_PX to EMOJI_MAX_PX font size,
 * shapes from SHAPE_MIN_PX to SHAPE_MAX_PX across, by strength.
 */
export function flowMarkerSize(s: Pick<FlowSignal, 'kind' | 'detail' | 'value'>, compact = false): number {
  const k = flowStrength(s);
  const emoji = flowGlyph(s).shape === 'emoji';
  if (compact) {
    return emoji
      ? (COMPACT_EMOJI_MIN_PX + k * (COMPACT_EMOJI_MAX_PX - COMPACT_EMOJI_MIN_PX)) / 2
      : (COMPACT_SHAPE_MIN_PX + k * (COMPACT_SHAPE_MAX_PX - COMPACT_SHAPE_MIN_PX)) / 2;
  }
  return emoji
    ? EMOJI_MIN_SIZE + k * (EMOJI_MAX_SIZE - EMOJI_MIN_SIZE)
    : MIN_SIZE + k * (MAX_SIZE - MIN_SIZE);
}

/** Centre distance from the price: the backing disc's near edge sits FLOW_GAP off it. */
export function flowOffset(size: number): number {
  return size + DISC_PAD + FLOW_GAP;
}

/**
 * The one device-pixel ratio for sizes (glyphs, discs, rings, badges), so a
 * glyph is never stretched when the two axes differ. Positions still map per
 * axis.
 */
export function flowSizeRatio(horizontalPixelRatio: number, verticalPixelRatio: number): number {
  const r = Math.min(horizontalPixelRatio, verticalPixelRatio);
  return Number.isFinite(r) && r > 0 ? r : 1;
}

/**
 * Store signals -> primitive items for one chart: drop kinds whose toggle is
 * off, fold into the displayed timeframe, colour by reading. Keeps the
 * store's ascending time order (place() relies on it).
 */
export interface FlowItemStyle {
  alpha: number;
  emphasis: boolean;
  tag: string | undefined;
  pulse: boolean;
}

export function toFlowItems(
  signals: readonly FlowSignal[],
  tfMinutes: number,
  shown: Partial<Record<FlowLevelKey, boolean>>,
  colors: Partial<Record<FlowLevelKey, { high: string; low: string }>>,
  /** TradeFinder UX: opacity, level emphasis + tag, pulse (tradeFinderUx.markerStyle). */
  style?: (s: FlowSignal) => FlowItemStyle,
  /** Pulse length from the signal's time, ms (with `style`). */
  pulseMs = 8_000,
  /** Phone / narrow chart: smaller glyphs, glow and labels. */
  compact = false,
): FlowItem[] {
  const out: FlowItem[] = [];
  for (const s of signals) {
    const key = flowToggleKey(s.kind);
    if (!key || !shown[key]) continue;
    const reading = flowReading(s);
    const c = colors[key] ?? { high: '#ff5370', low: '#2ee6a6' };
    const item: FlowItem = {
      id: s.id,
      time: flowBarTime(s.timestamp, tfMinutes),
      price: s.price,
      reading,
      glyph: flowGlyph(s),
      size: flowMarkerSize(s, compact),
      color: reading === 'bullish' ? c.low : c.high,
    };
    if (compact) item.compact = true;
    // Context kinds: the timer ring over the horizon the research measured.
    const holdS = contextHoldS(s);
    if (holdS !== null) item.hold = { startMs: s.timestamp, holdMs: holdS * 1000 };
    if (s.kind === 'queue_depletion') item.size *= DEPLETION_SCALE;
    if (style) {
      const st = style(s);
      const a = st.alpha;
      if (a < 1) item.alpha = Math.max(MIN_ALPHA, a);
      if (st.emphasis) {
        item.emphasis = true;
        item.size *= compact ? COMPACT_EMPHASIS_SCALE : EMPHASIS_SCALE;
      }
      if (st.tag) item.tag = st.tag;
      if (st.pulse) item.pulseUntil = s.timestamp + pulseMs;
    }
    out.push(item);
  }
  return out;
}

/**
 * The headless hook on the chart container: `data-flow-placed` (glyphs on
 * screen) and `data-flow-last` ("x,y" of the newest, '' for none). Written only
 * when a value changes, since place() runs on every render. Returns whether
 * anything was written.
 */
export function setFlowPlacedData(el: HTMLElement, placed: number, last: string): boolean {
  const count = String(placed);
  let wrote = false;
  if (el.dataset.flowPlaced !== count) {
    el.dataset.flowPlaced = count;
    wrote = true;
  }
  if (el.dataset.flowLast !== last) {
    el.dataset.flowLast = last;
    wrote = true;
  }
  return wrote;
}

// ------------------------------------------------------------- drawing

function path(ctx: CanvasRenderingContext2D, shape: FlowShape, x: number, y: number, r: number, up: boolean): void {
  const d = up ? -1 : 1; // apex direction on screen
  ctx.beginPath();
  switch (shape) {
    case 'diamond':
      ctx.moveTo(x, y - r); ctx.lineTo(x + r, y); ctx.lineTo(x, y + r); ctx.lineTo(x - r, y); ctx.closePath();
      break;
    case 'triangle':
      ctx.moveTo(x, y + d * r); ctx.lineTo(x + r, y - d * r); ctx.lineTo(x - r, y - d * r); ctx.closePath();
      break;
    case 'chevron': {
      const t = r * 0.55;
      ctx.moveTo(x, y + d * r); ctx.lineTo(x + r, y - d * (r - t)); ctx.lineTo(x + r, y - d * r);
      ctx.lineTo(x, y + d * (r - 2 * t)); ctx.lineTo(x - r, y - d * r); ctx.lineTo(x - r, y - d * (r - t)); ctx.closePath();
      break;
    }
    case 'doubleChevron':
      for (const off of [-r * 0.5, r * 0.5]) {
        const cy = y + off;
        const h = r * 0.5;
        const t = h * 0.6;
        ctx.moveTo(x, cy + d * h); ctx.lineTo(x + r, cy - d * (h - t)); ctx.lineTo(x + r, cy - d * h);
        ctx.lineTo(x, cy + d * (h - 2 * t)); ctx.lineTo(x - r, cy - d * h); ctx.lineTo(x - r, cy - d * (h - t)); ctx.closePath();
      }
      break;
    case 'circle':
      ctx.arc(x, y, r, 0, Math.PI * 2);
      break;
    case 'star':
      for (let i = 0; i < 10; i += 1) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const rr = i % 2 === 0 ? r * 1.15 : r * 0.5;
        const px = x + rr * Math.cos(a);
        const py = y + rr * Math.sin(a);
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      break;
    case 'square':
      ctx.rect(x - r * 0.85, y - r * 0.85, r * 1.7, r * 1.7);
      break;
    case 'hexagon':
      for (let i = 0; i < 6; i += 1) {
        const a = Math.PI / 6 + (i * Math.PI) / 3;
        const px = x + r * Math.cos(a);
        const py = y + r * Math.sin(a);
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      break;
    case 'bar':
      ctx.rect(x - r * 1.1, y - r * 0.45, r * 2.2, r * 0.9);
      break;
  }
}

export class FlowRenderer implements IPrimitivePaneRenderer {
  private _items: FlowPlaced[] = [];
  private _now = 0;

  /** `now`: the primitive's clock at place() time (pulse, flash and timer rings). */
  update(items: FlowPlaced[], now = Date.now()): void {
    this._items = items;
    this._now = now;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      const pr = flowSizeRatio(hr, vr);
      const now = this._now;
      for (const p of this._items) {
        const x = p.x * hr;
        const y = p.y * vr;
        const r = p.size * pr;
        // Dimming / fading applies to the disc and ring only: the glyph is
        // always drawn crisp (traces keep their own faint look).
        const glyphAlpha = p.glyph.faint ? 0.5 : 1;
        const alpha = glyphAlpha * (p.alpha ?? 1);
        // Every marker in its own save/restore: alpha, shadow and fonts never
        // leak into the next one (or into other primitives).
        ctx.save();
        // Backing disc + reading ring: contrast on any candle or background.
        ctx.globalAlpha = alpha;
        if (p.emphasis) {
          // Soft glow on the disc only; reset before the glyph so it stays crisp.
          ctx.shadowColor = p.color;
          ctx.shadowBlur = (p.compact ? COMPACT_GLOW_BLUR_PX : GLOW_BLUR_PX) * pr;
        }
        ctx.beginPath();
        ctx.arc(x, y, r + DISC_PAD * pr, 0, Math.PI * 2);
        ctx.fillStyle = DISC_FILL;
        ctx.fill();
        ctx.strokeStyle = p.color;
        ctx.lineWidth = (p.emphasis ? RING_PX * 1.4 : RING_PX) * pr;
        ctx.stroke();
        if (p.emphasis) {
          ctx.shadowBlur = 0;
          ctx.shadowColor = 'transparent';
        }
        ctx.globalAlpha = glyphAlpha;
        if (p.glyph.shape === 'emoji') {
          ctx.font = `${Math.round(r * 2)}px ${EMOJI_FONT}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = '#ffffff';
          ctx.fillText(p.glyph.symbol, x, y + 0.5 * pr);
        } else {
          path(ctx, p.glyph.shape, x, y, r * SHAPE_INSET, p.reading === 'bullish');
          if (p.glyph.hollow) {
            ctx.strokeStyle = p.color;
            ctx.lineWidth = 1.5 * pr;
            ctx.stroke();
          } else {
            ctx.fillStyle = p.color;
            ctx.fill();
          }
        }
        if (p.count > 1) {
          // Cluster count: a small superscript on the disc's top-right.
          ctx.globalAlpha = 1;
          const label = String(p.count);
          const sr = (SUPER_FONT_PX / 2 + 1.5) * pr;
          const sx = x + (r + DISC_PAD * pr) * 0.75;
          const sy = y - (r + DISC_PAD * pr) * 0.75;
          ctx.beginPath();
          ctx.arc(sx, sy, sr, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(15,23,42,0.95)';
          ctx.fill();
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 1 * pr;
          ctx.stroke();
          ctx.font = `bold ${SUPER_FONT_PX * pr}px Arial`;
          ctx.fillStyle = '#f1f5f9';
          ctx.textBaseline = 'middle';
          ctx.textAlign = 'center';
          ctx.fillText(label, sx, sy + 0.5 * pr);
        }
        if (p.tag) {
          // The level code only ("PW"): a compact pill right of the disc, inside the layout box.
          const fpx = p.compact ? COMPACT_TAG_FONT_PX : TAG_FONT_PX;
          ctx.globalAlpha = 1;
          ctx.font = `600 ${fpx * pr}px ${TAG_FONT}`;
          const lx = x + r + (DISC_PAD + RING_PX / 2 + FLOW_TAG_GAP) * pr;
          const w = ctx.measureText(p.tag).width + 2 * FLOW_TAG_PAD * pr;
          const h = (fpx + 4) * pr;
          ctx.fillStyle = 'rgba(10,15,30,0.8)';
          ctx.fillRect(lx, y - h / 2, w, h);
          ctx.fillStyle = p.color;
          ctx.fillRect(lx, y - h / 2, 1.5 * pr, h);
          ctx.fillStyle = '#f8fafc';
          ctx.textBaseline = 'middle';
          ctx.textAlign = 'left';
          ctx.fillText(p.tag, lx + FLOW_TAG_PAD * pr, y + 0.5 * pr);
        }
        if (p.hold) {
          // "->60s" timer ring: the remaining part of the hold, clockwise from 12 o'clock.
          const left = holdFraction(now, p.hold.startMs, p.hold.holdMs / 1000);
          if (left > 0) {
            ctx.globalAlpha = Math.max(0.6, alpha);
            ctx.beginPath();
            ctx.arc(x, y, r + (DISC_PAD + HOLD_RING_GAP) * pr, -Math.PI / 2, -Math.PI / 2 + left * Math.PI * 2);
            ctx.strokeStyle = p.color;
            ctx.lineWidth = HOLD_RING_PX * pr;
            ctx.lineCap = 'round';
            ctx.stroke();
          }
        }
        if (p.pulseUntil !== undefined && now < p.pulseUntil) {
          // Expanding ring from the disc edge, fading out each cycle.
          const phase = (now % PULSE_PERIOD_MS) / PULSE_PERIOD_MS;
          ctx.globalAlpha = 0.85 * (1 - phase);
          ctx.beginPath();
          ctx.arc(x, y, r + DISC_PAD * pr + phase * 12 * pr, 0, Math.PI * 2);
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 2 * pr;
          ctx.stroke();
        }
        if (p.flash) {
          // Strip click: a bright double ring that blinks.
          const on = Math.floor(now / 300) % 2 === 0;
          ctx.globalAlpha = on ? 1 : 0.35;
          ctx.beginPath();
          ctx.arc(x, y, r + (DISC_PAD + 4) * pr, 0, Math.PI * 2);
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2.5 * pr;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(x, y, r + (DISC_PAD + 8) * pr, 0, Math.PI * 2);
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 1.5 * pr;
          ctx.stroke();
        }
        ctx.restore();
      }
    });
  }
}

/** Level tag font: text first, colour emoji for the glyph. */
export const TAG_FONT = `system-ui,-apple-system,"Segoe UI",Roboto,sans-serif,${EMOJI_FONT}`;

/** Does anything on screen still animate (pulse ring or flash)? */
export function flowAnimating(placed: readonly FlowPlaced[], now: number): boolean {
  for (const p of placed) {
    if (p.flash) return true;
    if (p.pulseUntil !== undefined && now < p.pulseUntil) return true;
  }
  return false;
}

/** Is a timer ring still depleting on screen? */
export function flowHolding(placed: readonly FlowPlaced[], now: number): boolean {
  for (const p of placed) {
    if (p.hold && holdFraction(now, p.hold.startMs, p.hold.holdMs / 1000) > 0) return true;
  }
  return false;
}

class FlowPaneView implements IPrimitivePaneView {
  private _renderer = new FlowRenderer();
  constructor(private _source: FlowSignalMarkerPrimitive) {}
  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }
  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.place(), this._source.now());
    return this._renderer;
  }
}

/** First index whose time is >= t (items ascending by time). */
function lowerBound(items: readonly FlowItem[], t: number): number {
  let lo = 0;
  let hi = items.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (items[mid].time < t) lo = mid + 1; else hi = mid;
  }
  return lo;
}

/** One glyph on screen: a single signal, or a cluster's strongest with every id under it. */
interface FlowHead {
  head: FlowItem;
  ids: string[];
}

/** Group items [from, to) by candle and side, folding more than CLUSTER_AT into one head. */
function flowHeads(items: readonly FlowItem[], from: number, to: number): FlowHead[] {
  const groups = new Map<string, FlowItem[]>();
  for (let i = from; i < to; i += 1) {
    const it = items[i];
    const key = `${it.time}:${it.reading}`;
    const g = groups.get(key);
    if (g) g.push(it);
    else groups.set(key, [it]);
  }
  const out: FlowHead[] = [];
  for (const g of groups.values()) {
    if (g.length > CLUSTER_AT) out.push({ head: g.reduce((a, b) => (b.size > a.size ? b : a)), ids: g.map((i) => i.id) });
    else for (const i of g) out.push({ head: i, ids: [i.id] });
  }
  return out;
}

/**
 * The layout box of one glyph, relative to its centre: the disc and ring, plus
 * the count badge and the tag to its right when there are any.
 */
export function flowBox(
  size: number,
  count: number,
  tag: string | undefined,
  measure: (text: string, font: string) => number,
  compact = false,
  /** Has a timer ring (sweep_follow): the box covers it for good, so the layout never shifts when it runs out. */
  hold = false,
): { left: number; right: number; half: number } {
  const r = size + DISC_PAD + (hold ? HOLD_RING_GAP + HOLD_RING_PX / 2 : RING_PX / 2);
  let right = r;
  let half = r;
  if (count > 1) {
    // Superscript circle centred at 0.75 x the disc radius, up and right.
    const reach = (size + DISC_PAD) * 0.75 + SUPER_FONT_PX / 2 + 1.5 + 0.5;
    right = Math.max(right, reach);
    half = Math.max(half, reach);
  }
  if (tag) {
    const fpx = compact ? COMPACT_TAG_FONT_PX : TAG_FONT_PX;
    // The code pill: 4px padding each side of the text (FlowRenderer).
    right = Math.max(right, r + FLOW_TAG_GAP + measure(tag, `600 ${fpx}px ${TAG_FONT}`) + 2 * FLOW_TAG_PAD);
    half = Math.max(half, (fpx + 4) / 2);
  }
  return { left: -r, right, half };
}

export class FlowSignalMarkerPrimitive implements ISeriesPrimitive<Time> {
  private _items: FlowItem[] = [];
  private _placed: FlowPlaced[] = [];
  private _view = new FlowPaneView(this);
  private _layout = new MarkerLayoutClient('flow', (ctx) => this._layoutItems(ctx));
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _onPlaced?: (placed: { ids: string[]; x: number; y: number }[]) => void;
  private _flashId: string | null = null;
  private _flashUntil = 0;
  private _raf: number | null = null;
  private _holdTimer: ReturnType<typeof setTimeout> | null = null;
  private _clock: () => number = () => Date.now();
  private _lastNow = Date.now();

  /** Tests: a fixed clock for place() / the renderer (pulse, flash, timer rings). */
  setClock(clock: (() => number) | null): void {
    this._clock = clock ?? (() => Date.now());
  }

  /** The clock reading of the last place() (what the renderer draws with). */
  now(): number {
    return this._lastNow;
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    if (this._raf !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this._raf);
    this._raf = null;
    if (this._holdTimer !== null) clearTimeout(this._holdTimer);
    this._holdTimer = null;
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: lay out alone). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._view];
  }

  updateAllViews(): void {
    /* positions are recomputed on every render */
  }

  /** Items ascending by time (toFlowItems keeps the store order). */
  setItems(items: FlowItem[]): void {
    this._items = items;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  /** Boxes for the shared layout: one per glyph (or cluster) in view. */
  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const from = lowerBound(this._items, ctx.fromTime);
    const to = lowerBound(this._items, ctx.toTime + 1);
    const out: LayoutItem[] = [];
    for (const { head, ids } of flowHeads(this._items, from, to)) {
      const x = ctx.timeToX(head.time);
      const py = ctx.priceToY(head.price);
      if (x === null || py === null) continue;
      const box = flowBox(head.size, ids.length, head.tag, ctx.measure, head.compact, !!head.hold);
      const bull = head.reading === 'bullish';
      // Never on the candle: the stack starts at the bar's low (bullish) or
      // high (bearish) and a dotted leader runs back to the signal price.
      const bar = ctx.bar(head.time);
      const edge = bar ? ctx.priceToY(bull ? bar.low : bar.high) : null;
      out.push({
        key: ids.length > 1 ? `cluster:${head.time}:${head.reading}` : head.id,
        time: head.time,
        x,
        side: bull ? 'below' : 'above',
        anchorY: edge ?? py,
        leaderY: py,
        color: head.color,
        w: box.right - box.left,
        h: box.half * 2,
        dx: box.left,
        gap: FLOW_GAP,
        priority: LAYOUT_PRIORITY.flow,
        label: `${head.glyph.symbol} ${head.price.toFixed(2)}${ids.length > 1 ? ` (${ids.length})` : ''}`,
        ref: { head, ids } satisfies FlowHead,
      });
    }
    return out;
  }

  items(): FlowItem[] {
    return this._items;
  }

  /** Told where the glyphs landed on each render. */
  setOnPlaced(cb: ((placed: { ids: string[]; x: number; y: number }[]) => void) | undefined): void {
    this._onPlaced = cb;
  }

  /** Screen positions for the current render (also used for hit testing). */
  place(): FlowPlaced[] {
    const now = this._clock();
    this._lastNow = now;
    const out: FlowPlaced[] = [];
    if (!this._series || !this._chart) {
      this._placed = out;
      return out;
    }
    const shared = this._layout.placed();
    if (shared) {
      for (const p of shared) {
        const { head, ids } = p.item.ref as FlowHead;
        out.push(this._decorate({
          ids,
          x: p.item.x,
          y: p.top + p.h / 2,
          size: head.size,
          glyph: head.glyph,
          reading: head.reading,
          color: head.color,
          count: ids.length,
        }, head, ids, now));
      }
      this._placed = out;
      this._onPlaced?.(out.map((p) => ({ ids: p.ids, x: p.x, y: p.y })));
      this._scheduleAnimation(out, now);
      return out;
    }
    const ts = this._chart.timeScale();
    let from = 0;
    let to = this._items.length;
    const range = ts.getVisibleRange?.() as { from: number; to: number } | null | undefined;
    if (range && typeof range.from === 'number' && typeof range.to === 'number') {
      from = lowerBound(this._items, range.from);
      to = lowerBound(this._items, range.to + 1);
    }
    // Group by candle and side, in time order.
    const groups = new Map<string, FlowItem[]>();
    for (let i = from; i < to; i += 1) {
      const it = this._items[i];
      const key = `${it.time}:${it.reading}`;
      const g = groups.get(key);
      if (g) g.push(it);
      else groups.set(key, [it]);
    }
    for (const g of groups.values()) {
      const x = ts.timeToCoordinate(g[0].time as Time);
      if (x === null) continue;
      const shown = g.length > CLUSTER_AT
        ? [{ head: g.reduce((a, b) => (b.size > a.size ? b : a)), ids: g.map((i) => i.id) }]
        : g.map((i) => ({ head: i, ids: [i.id] }));
      for (const { head, ids } of shown) {
        const py = this._series.priceToCoordinate(head.price);
        if (py === null) continue;
        const off = flowOffset(head.size);
        const p: FlowPlaced = {
          ids,
          x,
          y: head.reading === 'bullish' ? py + off : py - off,
          size: head.size,
          glyph: head.glyph,
          reading: head.reading,
          color: head.color,
          count: ids.length,
        };
        out.push(this._decorate(p, head, ids, now));
      }
    }
    this._placed = out;
    this._onPlaced?.(out.map((p) => ({ ids: p.ids, x: p.x, y: p.y })));
    this._scheduleAnimation(out, now);
    return out;
  }

  /** TradeFinder styling onto a placed glyph (opacity, emphasis, tag, pulse, flash). */
  private _decorate(p: FlowPlaced, head: FlowItem, ids: string[], now: number): FlowPlaced {
    if (head.alpha !== undefined) p.alpha = head.alpha;
    if (head.emphasis) p.emphasis = true;
    if (head.tag) p.tag = head.tag;
    if (head.pulseUntil !== undefined) p.pulseUntil = head.pulseUntil;
    if (head.hold) p.hold = head.hold;
    if (head.compact) p.compact = true;
    if (this._flashId !== null && now < this._flashUntil && ids.includes(this._flashId)) p.flash = true;
    return p;
  }

  /** Blink the marker holding `id` for FLASH_MS (strip click). */
  flash(id: string, now = Date.now()): void {
    this._flashId = id;
    this._flashUntil = now + FLASH_MS;
    this._requestUpdate?.();
  }

  /** Keep repainting while a pulse or flash is on screen, then stop. */
  private _scheduleAnimation(placed: readonly FlowPlaced[], now: number): void {
    if (this._flashId !== null && now >= this._flashUntil) this._flashId = null;
    if (this._raf !== null || !this._requestUpdate) return;
    if (!flowAnimating(placed, now) && this._flashId === null) {
      // Only timer rings: a few repaints a second until the last one runs out.
      if (this._holdTimer === null && flowHolding(placed, now)) {
        this._holdTimer = setTimeout(() => {
          this._holdTimer = null;
          this._requestUpdate?.();
        }, HOLD_REPAINT_MS);
      }
      return;
    }
    const raf = typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb: FrameRequestCallback) => setTimeout(() => cb(Date.now()), 33) as unknown as number;
    this._raf = raf(() => {
      this._raf = null;
      this._requestUpdate?.();
    });
  }

  /** Glyph (or cluster) under a point, from the last render. */
  markerAt(x: number, y: number): FlowPlaced | null {
    let best: FlowPlaced | null = null;
    let bestD = Infinity;
    for (const p of this._placed) {
      const d = Math.hypot(x - p.x, y - p.y);
      const edge = p.size + DISC_PAD;
      const badge = p.count > 1 && x >= p.x + edge && x <= p.x + edge + 28 && Math.abs(y - p.y) <= (BADGE_FONT_PX + 4) / 2;
      if ((d <= flowHitRadius(p.size) || badge) && d < bestD) {
        best = p;
        bestD = d;
      }
    }
    return best;
  }
}
